import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect, ConnectedProps } from "react-redux";
import {
  Radio,
  MonitorPlay,
  Repeat,
  Search,
  Image as ImageIcon,
  X,
} from "lucide-react";
import { InputWithMediaInfo, SelectedMediaCard, Uploader, toast } from "..";
import {
  MediaModel,
  GetMediaApi,
  UpdateMediaApi,
  InputWithMediaInfoRef,
} from "@/database";
import { RootState } from "../../store";
import {
  callRegisterCustomMediaOnCms,
  callRegisterMediaOnCms,
} from "@/store/upload/upload.action";
import {
  setMediaDetails,
  emptyMediaDetails,
  emptyAssetDetails,
  setParentMediaList,
} from "@/store/media/media.slice";
import { showUploadProgress } from "@/store/app/app.slice";
import {
  addFile,
  setUploadedSuccessfully,
  updateFileProgress,
} from "@/store/upload/upload.slice";
import { getMedia, getSearchedListMedia } from "@/store/media/media.action";
import { UnifiedDateTimePicker } from "../DateInput/UnifiedDateTimePicker";
import { useDebounce, secondsToHms } from "@/utils";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import "./premiere.scss";

/** Parent search: published + ready (playable), aligned with list/detail playable rows. */
const premiereParentMediaListParams = {
  key: "parentid" as const,
  status: 5,
  state: 10,
  must_not: { "media_ref.rel": ["premiere", "embed"], content_type: 2 },
};

function mediaToSelectedCard(media: MediaModel) {
  return {
    label: media.id,
    value: (media as any).title ?? "",
    image:
      getThumbnailUrlUtil({
        media,
        id: media.images?.thumb || media.images?.portrait,
        source: media.source,
      }) || "",
    duration: secondsToHms(media.meta?.duration),
    thumb: !!generateBaseUrl(media),
  };
}

/* ── Types ─────────────────────────────────────────── */

type PremierePlaybackMode = "live" | "vod" | "livevod";

interface IPremiereFormValues {
  sourceInput: string;
  media_schedule: number;
  playbackMode: PremierePlaybackMode;
  loopCount: number;
}

interface IPremiereFormProps {
  closeModal: () => void;
  onPremiereReady?: (data: any) => void;
  openUpload?: () => void;
  setModalBtnDisabled?: (disabled: boolean) => void;
  setRpcPending?: (pending: boolean) => void;
  onAssetTypeChange?: (assetType: string) => void;
}

/* ── Validation ────────────────────────────────────── */

const toEpochMs = (t: number) => Math.trunc(Number(t) || 0);

const premiereSchema = Yup.object({
  sourceInput: Yup.string(),
  media_schedule: Yup.number()
    .required("Schedule is required")
    .min(1, "Schedule is required"),
  playbackMode: Yup.string().oneOf(["live", "vod", "livevod"]).required(),
  loopCount: Yup.number().when("playbackMode", {
    is: (v: string) => v === "live" || v === "livevod",
    then: (schema) => schema.min(1, "Must be at least 1").required("Required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

/* ── Component ─────────────────────────────────────── */

const PremiereForm = ({
  closeModal: _closeModal,
  onPremiereReady,
  callRegisterCustomMediaOnCms,
  callRegisterMediaOnCms,
  setMediaDetails,
  getMedia,
  getSearchedListMedia,
  setParentMediaList,
  parentMediaList,
  openUpload,
  setModalBtnDisabled,
  setRpcPending,
  onAssetTypeChange,
}: Props) => {
  const [inputSourceText, setInputSourceText] = useState("");
  const debouncedInputSourceText = useDebounce(inputSourceText, 200);
  const parentMediaInputRef = useRef<InputWithMediaInfoRef>(null);
  const [selectedSourceVideo, setSelectedSourceVideo] = useState<any>(null);
  const [isSourceEdit, setIsSourceEdit] = useState(false);

  const [mediaData, setMediaData] = useState<MediaModel | null>(null);
  const [isValidatingId, setIsValidatingId] = useState(false);

  const [borrowEnabled, setBorrowEnabled] = useState(true);
  const [borrowedMetadata, setBorrowedMetadata] = useState<any>(null);

  const [posterFile, setPosterFile] = useState<any>(null);
  const [posterPreview, setPosterPreview] = useState<string | null>(null);
  const [posterHovered, setPosterHovered] = useState(false);
  const [posterError, setPosterError] = useState(false);
  const [isRpcPending, setIsRpcPending] = useState(false);

  // Holds the media.register response so onPosterFileEvent can use it
  const mediaRespRef = useRef<any>(null);
  // Holds pending poster file+cb if user picks poster before submit finishes
  const pendingPosterRef = useRef<{ file: any; cb: any } | null>(null);

  const formik = useFormik<IPremiereFormValues>({
    initialValues: {
      sourceInput: "",
      media_schedule: 0,
      playbackMode: "livevod",
      loopCount: 1,
    },
    validationSchema: premiereSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      const scheduleEpochMs = toEpochMs(values.media_schedule);

      const commonPremierePayload = {
        parentMediaId: mediaData?.id || null,
        sourceInput: values.sourceInput || "",
        selectedFile: null,
        posterFile: posterFile || null,
        borrowedMetadata: borrowedMetadata || null,
        media_schedule: scheduleEpochMs,
        playbackMode: values.playbackMode,
        loopCount: values.playbackMode === "vod" ? undefined : values.loopCount,
      };

      const ts = Date.now();
      const sourceId = values.sourceInput || "";
      const fid = sourceId ? `${sourceId}-${ts}` : String(ts);
      const registerName = `media${ts}`;
      const premiereTitle =
        borrowEnabled && borrowedMetadata?.title
          ? borrowedMetadata.title
          : (mediaData as any)?.title || "Premiere";

      const premiereDesc =
        borrowEnabled && borrowedMetadata?.description
          ? borrowedMetadata.description
          : "";

      if (!posterFile) {
        setPosterError(true);
        return;
      }

      setIsRpcPending(true);
      setRpcPending?.(true);

      callRegisterCustomMediaOnCms(
        {
          type: "premiere",
          data: {},
          name: registerName,
          id: sourceId,
          title: premiereTitle,
          islive: 0,
          fid,
          desc: premiereDesc,
          asset_type: "premiere",
          source: "cms",
          media_schedule: scheduleEpochMs,
          playback_mode: values.playbackMode,
          loop_count: values.playbackMode === "vod" ? 0 : values.loopCount,
          is_borrow: Boolean(borrowEnabled && borrowedMetadata),
          ctype: "premiere",
        },
        (resp: any) => {
          setIsRpcPending(false);
          setRpcPending?.(false);
          if (resp.status === 200 && resp.data) {
            const newId = resp.data.id;

            // Store response so poster upload can use cdn/damid/business
            mediaRespRef.current = resp.data;

            // If poster was already picked, trigger its upload now
            if (pendingPosterRef.current) {
              const { file, cb } = pendingPosterRef.current;
              pendingPosterRef.current = null;
              triggerPosterUpload(file, cb, resp.data);
            }

            setTimeout(() => {
              getMedia({ id: newId });
            }, 100);

            // getMedia({ id: newId });
            // setMediaDetails({
            //   data: {
            //     media: {
            //       ...resp.data,
            //       creditsData: mediaData?.creditsData || [],
            //       parentData: mediaData?.parentData || null,
            //     },
            //   },
            // });
            onPremiereReady?.({
              ...commonPremierePayload,
              registeredPremiereId: newId,
            });
            openUpload?.();
          } else {
            toast.fail(resp?.error || "Could not register premiere.");
          }
        }
      );
    },
  });

  // Mirrors exactly what ThumbnailSelectionModal does: call callRegisterMediaOnCms
  // with name:"thumb" so it hits assets.register, then fire cb(query, jwt).
  const triggerPosterUpload = (file: any, cb: any, mediaResp: any) => {
    const resumableFile = {
      ...file,
      isShorts: false,
      type: "thumb",
      name: "thumb",
      id: mediaResp.id,
      damid: mediaResp.damid,
      cdn: (mediaResp.cdn ?? []).map((c: { id: string }) => ({ id: c.id })),
      business: mediaResp.business,
    };

    callRegisterMediaOnCms(resumableFile, (response: any) => {
      if (response && response.status === 200) {
        cb({ entity: mediaResp.id, key: "thumb" }, response.data.jwt);
      }
    });
  };

  useEffect(() => {
    onAssetTypeChange?.("premiere");
  }, [onAssetTypeChange]);

  useEffect(() => {
    const hasSchedule = formik.values.media_schedule >= 1;
    const loopOk =
      formik.values.playbackMode === "vod" ||
      (formik.values.loopCount >= 1 && !formik.errors.loopCount);
    const canProceed =
      hasSchedule && loopOk && !formik.errors.media_schedule && !isRpcPending;
    setModalBtnDisabled?.(!canProceed);
  }, [
    formik.values.media_schedule,
    formik.values.playbackMode,
    formik.values.loopCount,
    formik.errors.media_schedule,
    formik.errors.loopCount,
    isRpcPending,
    setModalBtnDisabled,
  ]);

  useEffect(() => {
    if (debouncedInputSourceText.length >= 3) {
      getSearchedListMedia({
        ...premiereParentMediaListParams,
        stitle: debouncedInputSourceText,
      });
    } else {
      setParentMediaList({
        data: { list: [], count: 0 },
        error: null,
        isLoading: false,
      });
    }
  }, [debouncedInputSourceText, getSearchedListMedia, setParentMediaList]);

  useEffect(() => {
    const handleNext = () => formik.submitForm();
    window.addEventListener("nextClickPremiere", handleNext);
    return () => window.removeEventListener("nextClickPremiere", handleNext);
  }, [formik]);

  const handleOpenSourceMenu = () => {
    setTimeout(() => parentMediaInputRef.current?.openMenu(), 0);
  };

  const handleResetSource = () => {
    setMediaData(null);
    setSelectedSourceVideo(null);
    setIsSourceEdit(false);
    setBorrowedMetadata(null);
    setBorrowEnabled(true);
    formik.setFieldValue("sourceInput", "");
  };

  const validateAndSetSource = async (mediaId: string) => {
    setIsValidatingId(true);
    try {
      const res = await GetMediaApi({ id: mediaId });
      if (
        res.status === 200 &&
        res.data.source !== "embed" &&
        res.data?.media_ref?.rel !== "simulive" &&
        res.data?.media_ref?.rel !== "chapter"
      ) {
        setMediaData(res.data);
        setSelectedSourceVideo(mediaToSelectedCard(res.data));
        setIsSourceEdit(false);
        formik.setFieldValue("sourceInput", res.data.id);
        setBorrowEnabled(true);
        const md = res.data as any;
        setBorrowedMetadata({
          title: md.title ?? "",
          description: md.description ?? "",
          tags: md.tags ?? [],
          thumbnails: md.base64 ?? md.thumb ?? null,
        });
      } else if (res.status === 200 && res.data.source === "embed") {
        toast.fail("Embed content cannot be used for Premiere.");
        handleResetSource();
      } else if (
        res.status === 200 &&
        res.data?.media_ref?.rel === "simulive"
      ) {
        toast.fail("Simulive content cannot be used for Premiere.");
        handleResetSource();
      } else {
        toast.fail("Could not load the selected source.");
        handleResetSource();
      }
    } catch {
      toast.fail("Could not validate the source.");
      handleResetSource();
    }
    setIsValidatingId(false);
  };

  const handleParentMediaChange = (newValue: any) => {
    if (!newValue?.label) {
      handleResetSource();
      return;
    }
    void validateAndSetSource(String(newValue.label));
  };

  const handleBorrowToggle = (checked: boolean) => {
    setBorrowEnabled(checked);
    if (checked && mediaData) {
      const md = mediaData as any;
      setBorrowedMetadata({
        title: md.title ?? "",
        description: md.description ?? "",
        tags: md.tags ?? [],
        thumbnails: md.base64 ?? md.thumb ?? null,
      });
    } else {
      setBorrowedMetadata(null);
    }
  };

  const handleRemovePoster = () => {
    setPosterFile(null);
    setPosterPreview(null);
    pendingPosterRef.current = null;
  };

  const onPosterFileEvent = (event: string, data: any, cb?: any) => {
    if (event === "fileAdded") {
      const file = data.file;
      if (!file.file.type.startsWith("image/")) {
        toast.fail("Please upload an image file for the poster.");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target) setPosterPreview(e.target.result as string);
      };
      reader.readAsDataURL(file.file);
      setPosterFile(file);
      setPosterError(false);

      if (mediaRespRef.current) {
        triggerPosterUpload(file, cb, mediaRespRef.current);
      } else {
        pendingPosterRef.current = { file, cb };
      }
    } else if (event === "fileSuccess") {
      const response = JSON.parse(data.response);
      const thumbId = response?.data?.id;
      const base = response?.data?.baseurl;
      const mediaId = mediaRespRef.current?.id;

      if (thumbId && mediaId) {
        const currentMedia = mediaRespRef.current;
        const existingImages = (currentMedia as any)?.images ?? {};
        UpdateMediaApi({
          ...(currentMedia as any),
          id: mediaId,
          is_short: 0,
          asset_type: "video",
          ctype: "premiere",
          images: { ...existingImages, premiere: thumbId, base: base },
        } as any).then((resp) => {
          if (resp.status === 200) {
            mediaRespRef.current = {
              ...currentMedia,

              images: { ...existingImages, premiere: thumbId, base: base },
            };
            setMediaDetails({
              data: {
                media: {
                  ...resp.data,
                },
              },
            });
            // getMedia({ id: mediaId });
          }
        });
      }
    }
  };

  const hasLoop =
    formik.values.playbackMode === "live" ||
    formik.values.playbackMode === "livevod";

  const playbackModes: {
    value: PremierePlaybackMode;
    label: string;
    desc: string;
    Icon: React.ElementType;
  }[] = [
    {
      value: "livevod",
      label: "Live + Replay",
      desc: "Goes live as scheduled, then auto-replays for late viewers.",
      Icon: Repeat,
    },
    {
      value: "live",
      label: "Live Only",
      desc: "Scheduled live broadcast. No replay after end.",
      Icon: Radio,
    },
    {
      value: "vod",
      label: "Video Only",
      desc: "Plays as a normal on-demand video. Always available.",
      Icon: MonitorPlay,
    },
  ];

  return (
    <div className="ps-1 mt-3">
      <div className="d-flex gap-2 border mb-3">
        {/* ── Left: Mandatory fields (Playback + Schedule) ── */}
        <div className="d-flex flex-grow-1 border-end p-3 flex-column gap-4">
          {/* Schedule */}
          <div className="row">
            <UnifiedDateTimePicker
              mode="datetime"
              onChange={(date: number | null) => {
                formik.setFieldValue("media_schedule", date || 0);
                formik.setFieldTouched("media_schedule", true);
              }}
              value={formik.values.media_schedule}
              outputFormat="timestamp"
              fromSimulive={true}
              dateLabel="Start Date"
              timeLabel="Start Time"
              width="100%"
              datePickerPlacement="bottom-start"
              timePickerPlacement="bottom-start"
            />
            {formik.touched.media_schedule && formik.errors.media_schedule && (
              <div className="text-danger small mt-1">
                {formik.errors.media_schedule}
              </div>
            )}
          </div>

          <div>
            <label className="p-1">PLAYBACK MODE</label>
            <div
              className="premiere-mode-row"
              role="radiogroup"
              aria-label="Playback mode"
            >
              {playbackModes.map(({ value, label, Icon }) => {
                const active = formik.values.playbackMode === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`premiere-mode-chip${active ? " premiere-mode-chip--active" : ""}`}
                    onClick={() => formik.setFieldValue("playbackMode", value)}
                  >
                    <Icon size={13} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
            <p className="premiere-mode-hint" aria-live="polite">
              {
                playbackModes.find(
                  (m) => m.value === formik.values.playbackMode
                )?.desc
              }
            </p>
          </div>

          <div
            className={`premiere-loop-wrap ${hasLoop ? "premiere-loop-wrap--visible" : ""}`}
          >
            <div>
              <label className="p-1">LIVE LOOP COUNT</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="number"
                  className="form-control form-control-sm text-center"
                  style={{ width: "64px" }}
                  name="loopCount"
                  min={1}
                  value={formik.values.loopCount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span className="text-muted small">times</span>
                {mediaData?.meta?.duration && formik.values.media_schedule ? (
                  <span className="text-muted small ms-auto">
                    Total:{" "}
                    {secondsToHms(
                      mediaData.meta.duration * formik.values.loopCount
                    )}
                  </span>
                ) : null}
              </div>
              {formik.touched.loopCount && formik.errors.loopCount && (
                <div className="text-danger small mt-1">
                  {formik.errors.loopCount}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Right: Optional fields (Source + Poster) ── */}
        <div
          className="d-flex flex-column gap-3 px-3 py-3"
          style={{ flex: "0 0 320px" }}
        >
          {/* Parent video (same control as MediaDetailsForm) */}
          <div>
            {selectedSourceVideo && !isSourceEdit ? (
              <div className="mt-1 d-flex flex-column gap-1">
                <label className="form-label mb-1">Parent Video</label>
                <SelectedMediaCard
                  media={selectedSourceVideo}
                  type="pre"
                  onClose={handleResetSource}
                  onEdit={() => {
                    setIsSourceEdit(true);
                    handleOpenSourceMenu();
                  }}
                  showEditButton={true}
                  showDeleteButton={true}
                  showPreviewButton={true}
                  cardStyle={{ width: "80px", minWidth: "80px" }}
                  style={{ width: "220px" }}
                  maxLength={20}
                />
                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="borrowMeta"
                    checked={borrowEnabled}
                    onChange={(e) => handleBorrowToggle(e.target.checked)}
                  />
                  <label
                    className="form-check-label small"
                    htmlFor="borrowMeta"
                  >
                    Borrow metadata from source
                  </label>
                </div>
              </div>
            ) : (
              <div className="position-relative">
                <Search className="searchInput" size={18} />
                <InputWithMediaInfo
                  ref={parentMediaInputRef}
                  label="SOURCE"
                  value={formik.values.sourceInput}
                  handleChange={(newValue: any) =>
                    handleParentMediaChange(newValue)
                  }
                  options={parentMediaList}
                  onInputChangeCallback={(params) => {
                    setInputSourceText(params.keyword);
                    if (params.keyword.length === 0) {
                      setParentMediaList({
                        data: { list: [], count: 0 },
                        error: null,
                        isLoading: false,
                      });
                    }
                  }}
                  handleBlur={() => {}}
                  menuPlacement="bottom"
                  menuPortalTarget={document.body}
                  menuStyles={{ right: 0, left: "auto" }}
                  maxMenuHeight={245}
                  hideArrow={true}
                  touched={false}
                  initialStructuredData={selectedSourceVideo}
                  initialInputText={
                    isSourceEdit && selectedSourceVideo
                      ? String(selectedSourceVideo.value ?? "")
                      : inputSourceText
                  }
                  customStyling={{ padding: "0 40px 0 20px" }}
                  placeholder="Search Title, Id"
                  loader={true}
                  showRequired={false}
                  disabled={isValidatingId}
                />
                {isValidatingId ? (
                  <div
                    className="position-absolute end-0 top-50 translate-middle-y me-3"
                    style={{ marginTop: "10px" }}
                  >
                    <i className="spinner-border spinner-border-sm text-primary" />
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Poster */}
          <div>
            <label className="p-1">
              Poster <span className="text-danger">*</span>
            </label>
            {posterError && (
              <div className="text-danger small mt-1 mb-1">
                Poster is required
              </div>
            )}
            <div
              className="ratio ratio-16x9 border rounded bg-light position-relative overflow-hidden"
              onMouseEnter={() => setPosterHovered(true)}
              onMouseLeave={() => setPosterHovered(false)}
            >
              {posterPreview ? (
                <>
                  <img
                    src={posterPreview}
                    alt="Poster"
                    style={{
                      objectFit: "contain",
                      width: "100%",
                      height: "100%",
                    }}
                    className="rounded"
                  />
                  {posterHovered && (
                    <div
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center gap-3 text-white blackTransparent rounded"
                      style={{ zIndex: 2 }}
                    >
                      <Uploader
                        id="premiere-poster-change"
                        datatype="IMAGE"
                        onFileEvent={onPosterFileEvent}
                      >
                        <div
                          className="d-flex flex-column align-items-center gap-1"
                          style={{ cursor: "pointer" }}
                        >
                          <ImageIcon size={20} />
                          <span style={{ fontSize: "13px" }}>CHANGE</span>
                        </div>
                      </Uploader>
                      <div
                        className="d-flex flex-column align-items-center gap-1"
                        style={{ cursor: "pointer" }}
                        onClick={handleRemovePoster}
                      >
                        <X size={20} />
                        <span style={{ fontSize: "13px" }}>REMOVE</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Uploader
                  id="premiere-poster"
                  datatype="IMAGE"
                  onFileEvent={onPosterFileEvent}
                >
                  <div
                    className="d-flex flex-column align-items-center justify-content-center h-100 w-100 text-secondary"
                    style={{ cursor: "pointer" }}
                  >
                    <ImageIcon size={28} />
                  </div>
                </Uploader>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Redux ─────────────────────────────────────────── */

const mapState = (state: RootState) => ({
  parentMediaList: state.media.parentMediaList.list,
});
const mapDispatch = {
  callRegisterCustomMediaOnCms,
  callRegisterMediaOnCms,
  setMediaDetails,
  showUploadProgress,
  addFile,
  setUploadedSuccessfully,
  updateFileProgress,
  getMedia,
  emptyMediaDetails,
  emptyAssetDetails,
  getSearchedListMedia,
  setParentMediaList,
};
const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector> & IPremiereFormProps;

export default connector(PremiereForm);
